/** Native details remain usable without JavaScript; enhancement adds reversible height animation. */
export function initAccordion(root: HTMLElement) {
    if (root.dataset.accordionInitialized === 'true') return;
    const items = Array.from(root.children).filter((element): element is HTMLDetailsElement =>
        element instanceof HTMLDetailsElement && element.classList.contains('stgb-accordion-item'));
    if (!items.length) return;
    root.dataset.accordionInitialized = 'true';
    const multiple = root.dataset.multiple === 'true';
    const duration = Math.max(150, Math.min(600, Number(root.dataset.duration) || 320));
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controllers: Array<{summary: HTMLElement; isOpen: () => boolean; setOpen: (open: boolean, animate?: boolean) => void}> = [];

    items.forEach(item => {
        const summary = item.querySelector<HTMLElement>(':scope > .stgb-accordion-summary');
        const panel = item.querySelector<HTMLElement>(':scope > .stgb-accordion-panel');
        if (!summary || !panel) return;
        let expanded = item.open && (multiple || !controllers.some(controller => controller.isOpen()));
        let animation: Animation | null = null;

        const finish = () => {
            item.open = expanded;
            item.classList.remove('is-animating');
            if (animation) {
                animation.onfinish = null;
                animation.cancel();
                animation = null;
            }
        };

        const setOpen = (open: boolean, animate = true) => {
            if (open === expanded && animate) return;
            // Read the current animated height before cancellation, so rapid clicks reverse smoothly.
            const startHeight = item.open ? panel.getBoundingClientRect().height : 0;
            if (animation) {
                animation.onfinish = null;
                animation.cancel();
                animation = null;
            }
            if (!open && panel.contains(document.activeElement)) summary.focus({preventScroll: true});
            expanded = open;
            item.dataset.expanded = String(open);
            summary.setAttribute('aria-expanded', String(open));
            panel.inert = !open;
            panel.setAttribute('aria-hidden', String(!open));
            if (!animate || motion.matches || typeof panel.animate !== 'function') {
                finish();
                return;
            }
            // Keep details open during the closing animation; restore natural height when it finishes.
            item.open = true;
            const endHeight = open ? panel.scrollHeight : 0;
            if (Math.abs(startHeight - endHeight) < 1) {
                finish();
                return;
            }
            item.classList.add('is-animating');
            animation = panel.animate([{height: `${startHeight}px`}, {height: `${endHeight}px`}], {
                duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            });
            animation.onfinish = finish;
        };

        const controller = {summary, isOpen: () => expanded, setOpen};
        controllers.push(controller);
        setOpen(expanded, false);
        summary.addEventListener('click', event => {
            event.preventDefault();
            const next = !expanded;
            if (next && !multiple) controllers.forEach(other => { if (other !== controller) other.setOpen(false); });
            setOpen(next);
        });
        summary.addEventListener('keydown', event => {
            if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
            const index = controllers.indexOf(controller);
            const target = event.key === 'ArrowDown' ? (index + 1) % controllers.length
                : event.key === 'ArrowUp' ? (index + controllers.length - 1) % controllers.length
                : event.key === 'Home' ? 0 : event.key === 'End' ? controllers.length - 1 : -1;
            if (target < 0) return;
            event.preventDefault();
            controllers[target].summary.focus();
        });
        // Account for native toggles from browser find-in-page or scripts, without fighting our own animation.
        item.addEventListener('toggle', () => {
            if (animation || item.open === expanded) return;
            if (item.open && !multiple) controllers.forEach(other => { if (other !== controller) other.setOpen(false); });
            setOpen(item.open, false);
        });
        motion.addEventListener('change', () => { if (motion.matches) setOpen(expanded, false); });
    });
}
